/**
 * Theme preference: **System / Light / Dark**.
 *
 * Tri-state on purpose. A binary toggle cannot express "follow the OS", which is both the correct default and the
 * only state that keeps tracking when the user later changes their OS setting. `system` is represented by the
 * ABSENCE of `data-theme`, so `tokens.css`'s `prefers-color-scheme` block stays in charge.
 *
 * First paint is handled by `public/theme-init.js` (render-blocking, before the stylesheet). This module owns
 * changes made *after* boot, and must agree with it on the storage key.
 */
export const THEME_STORAGE_KEY = "arc-theme";
export const THEMES = ["system", "light", "dark"] as const;
export type Theme = (typeof THEMES)[number];

export function isTheme(v: unknown): v is Theme {
  return typeof v === "string" && (THEMES as readonly string[]).includes(v);
}

/** The stored preference, or `system` when absent/unreadable. Never throws — a theme must not break the app. */
export function readTheme(storage: Pick<Storage, "getItem"> = safeStorage()): Theme {
  try {
    const v = storage.getItem(THEME_STORAGE_KEY);
    return isTheme(v) ? v : "system";
  } catch {
    return "system";
  }
}

/** Apply + persist. `system` removes the attribute so `prefers-color-scheme` resumes control. */
export function applyTheme(theme: Theme, doc: Document = document, storage = safeStorage()): void {
  if (theme === "system") doc.documentElement.removeAttribute("data-theme");
  else doc.documentElement.setAttribute("data-theme", theme);
  try {
    storage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* storage disabled — the theme still applies for this session. */
  }
}

/** Storage can throw on access alone (Safari private mode), so every use is guarded. */
function safeStorage(): Pick<Storage, "getItem" | "setItem"> {
  try {
    return window.localStorage;
  } catch {
    return { getItem: () => null, setItem: () => undefined };
  }
}
